import {Injectable, inject} from '@angular/core';
import {ApiService, ProjectDetailsDraftService, ToastService, UserService} from '../';
import {IPageableProjects, IProject, IProjectMandateDetails, IUser, LibraryEntry, Project, ProjectDetails, ProjectLibraryItem, ProjectMandateDetails, QuickscanResult, IQuickscanResultData} from '../../models';
import {TranslateService} from '@ngx-translate/core';
import { HttpErrorResponse, HttpResponse, HttpSentEvent } from '@angular/common/http';
import {IProjectDetailsData, IProjectLibraryItemData} from '../../models/project/project-data';
import {Member} from '../../models/project/project';
import {ConstructionCosts} from '../../enums';
import {saveAs} from 'file-saver';
import {createCoalescingBuffer, CoalescingBuffer} from '../../utils';
import {MsAuthService} from '../../module/ms-auth';

@Injectable()
export class ProjectService {
    private readonly SAVE_DETAILS_DEBOUNCE_MS = 200;

    private apiService = inject(ApiService);
    private toastService = inject(ToastService);
    private translateService = inject(TranslateService);
    private userService = inject(UserService);
    private projectDetailsDraftService = inject(ProjectDetailsDraftService);
    private msAuthService = inject(MsAuthService);

    private userInfo: IUser;

    private saveDetailsBuffer: CoalescingBuffer<number, ProjectDetails, ProjectDetails> =
        createCoalescingBuffer(
            (latest) => this.putProjectDetails(latest),
            this.SAVE_DETAILS_DEBOUNCE_MS
        );

    constructor() {
        const userService = this.userService;

        userService.onUserInfoChange().subscribe((userInfo) => {
            this.userInfo = userInfo;
        });
        userService.getUserInfo().then((userInfo) => {
            this.userInfo = userInfo;
        });
    }

    public getProjects(page: number, pageSize: number, searchTerm?: string, searchCompany?: string, searchStatuses?: string[], searchClassifications?: string[],
                       searchConstructionCosts?: string): Promise<IPageableProjects> {
        return new Promise((resolve, reject) => {
            let additionalParameters = (searchTerm ? `&searchTerm=${encodeURIComponent(searchTerm)}` : '');
            additionalParameters += (searchCompany ? `&searchCompanyName=${encodeURIComponent(searchCompany)}` : '');
            additionalParameters += (searchStatuses && searchStatuses.length > 0 ? `&searchStatuses=${encodeURIComponent(searchStatuses.join('||'))}` : '');
            additionalParameters += (searchClassifications && searchClassifications.length > 0 ? `&searchClassifications=${encodeURIComponent(searchClassifications.join('||'))}` : '');
            if (searchConstructionCosts) {
                if (searchConstructionCosts === ConstructionCosts.LESS_THAN_10M) {
                    additionalParameters += '&upperBoundConstructionCosts=9999999';
                } else if (searchConstructionCosts === ConstructionCosts.BETWEEN_10M_AND_25M) {
                    additionalParameters += '&lowerBoundConstructionCosts=10000000&upperBoundConstructionCosts=24999999';
                } else if (searchConstructionCosts === ConstructionCosts.BETWEEN_25M_AND_50M) {
                    additionalParameters += '&lowerBoundConstructionCosts=25000000&upperBoundConstructionCosts=50000000';
                } else if (searchConstructionCosts === ConstructionCosts.GREATER_THAN_50M) {
                    additionalParameters += '&lowerBoundConstructionCosts=50000001';
                }
            }
            this.apiService.get(`/project?page=${page}&pageSize=${pageSize}${additionalParameters}`).then((projectResponse: HttpResponse<IPageableProjects>) => {
                projectResponse.body.content = projectResponse.body.content.map(data => new Project(data));
                resolve(projectResponse.body);
            }).catch((error) => {
                this.toastService.openError(error.status, this.translateService.instant('ERRORS.projects_not_updated'));
                reject(error);
            });
        });
    }

    public createProject(data): Promise<string> {
        return new Promise((resolve, reject) => {
            this.apiService.post('/project', data).then((_: HttpSentEvent) => {
                resolve('ok');
            }).catch((error) => {
                this.toastService.handleError(error, 'ERRORS.project_not_created');
                reject(error);
            });
        });
    }

    public changeProjectType(projectId: number, newProjectTypeId: number): Promise<ProjectDetails> {
        return new Promise((resolve, reject) => {
            this.apiService.put(`/projects/${projectId}/type/${newProjectTypeId}`, null)
                .then((projectResponse: HttpResponse<IProjectDetailsData>) => {
                    const project: ProjectDetails = new ProjectDetails(projectResponse.body);
                    resolve(project);
                }).catch((error) => {
                this.toastService.handleError(error, 'ERRORS.project_type_not_changed');
                reject(error);
            });
        });
    }

    public getProjectDetails(projectId: number): Promise<ProjectDetails> {
        return new Promise((resolve, reject) => {
            this.apiService.get(`/project/${projectId}/details`).then((projectResponse: HttpResponse<IProjectDetailsData>) => {
                const project: ProjectDetails = new ProjectDetails(projectResponse.body);
                resolve(project);
            }).catch((error) => {
                this.toastService.openError(error.status, this.translateService.instant('ERRORS.project_not_found'));
                reject(error);
            });
        });
    }

    public saveProjectDetails(projectDetails: ProjectDetails): Promise<ProjectDetails> {
        return this.saveDetailsBuffer.push(projectDetails.id, projectDetails);
    }

    /**
     * Fired from `pagehide` when the tab/browser is actually closing. HttpClient's XHR gets
     * aborted mid-flight on unload, so this bypasses it with a raw keepalive fetch, which browsers
     * guarantee to deliver after the page is gone. keepalive requests are capped around
     * combined, so a project with a large thumbnailBase64 can still silently fail to persist here -
     * that's a browser-level limit, not something this call can work around.
     */
    public saveProjectDetailsOnUnload(projectDetails: ProjectDetails): void {
        const url = `${this.apiService.requestUri}/project/${projectDetails.id}/details`;

        fetch(url, {
            method: 'PUT',
            keepalive: true,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${this.msAuthService.getAccessToken()}`
            },
            body: JSON.stringify(projectDetails.toApiModel())
        }).catch(() => {
            // Page is already unloading; there is no user left to show an error to
        });
    }

    private async putProjectDetails(projectDetails: ProjectDetails): Promise<ProjectDetails> {
        return this.apiService.put(`/project/${projectDetails.id}/details`, projectDetails.toApiModel())
            .then((projectResponse: HttpResponse<IProjectDetailsData>) => {
                this.projectDetailsDraftService.clear(projectDetails.id);
                return new ProjectDetails(projectResponse.body);
            })
            .catch((error) => {
                if (error instanceof HttpErrorResponse && error.status === 400) {
                    this.toastService.handleHttpError(error);
                } else if (error.status === 413) {
                    this.toastService.openError(error.status, this.translateService.instant('ERRORS.filesize_too_high'));
                } else {
                    this.toastService.openError(error.status, this.translateService.instant('ERRORS.project_not_updated'));
                }
                throw error;
            });
    }

    public getQuickscanResult(projectDetails: ProjectDetails, forTis: boolean, persist = false): Promise<QuickscanResult> {
        return new Promise((resolve, reject) => {
            let url = `/project/${projectDetails.id}/calculate?forTis=${forTis}`;
            if (persist) {
                url += '&persist=true';
            }

            this.apiService.post(url, projectDetails.toApiModel()).then((calculationResponse: HttpResponse<IQuickscanResultData>) => {
                resolve(new QuickscanResult(calculationResponse.body));
            }).catch((error) => {
                this.toastService.handleError(error, 'ERRORS.quickscan_result_not_retrieved');
                reject(error);
            });
        });
    }

    public addSelectedItems(projectDetails: ProjectDetails, libraryType: string, selectedItems: LibraryEntry[]): Promise<ProjectDetails> {
        return new Promise((resolve, reject) => {
            this.apiService.put(`/project/${projectDetails.id}/add-library-items/${libraryType}`, selectedItems).then((projectResponse: HttpResponse<IProjectDetailsData>) => {
                const project: ProjectDetails = new ProjectDetails(projectResponse.body);
                resolve(project);
            }).catch((error) => {
                this.toastService.handleError(error, `ERRORS.library_type.${libraryType}.not_updated`);
                reject(error);
            });
        });
    }

    public addProjectLibraryItem(projectDetails: ProjectDetails, libraryType: string, specificLibraryItem: ProjectLibraryItem): Promise<ProjectDetails> {
        return new Promise((resolve, reject) => {
            this.apiService.put(`/project/${projectDetails.id}/add-project-library-item/${libraryType}`, specificLibraryItem).then((projectResponse: HttpResponse<IProjectDetailsData>) => {
                const project: ProjectDetails = new ProjectDetails(projectResponse.body);
                resolve(project);
            }).catch((error) => {
                this.toastService.handleError(error, `ERRORS.library_type.${libraryType}.not_updated`);
                reject(error);
            });
        });
    }

    public updateProjectLibraryItem(projectLibraryItem: ProjectLibraryItem): Promise<ProjectLibraryItem> {
        return new Promise((resolve, reject) => {
            this.apiService.put(`/project/update-library-items/${projectLibraryItem.id}`, {id: undefined, ...projectLibraryItem}).then((response: HttpResponse<IProjectLibraryItemData>) => {
                resolve(new ProjectLibraryItem(response.body));
            }).catch((error) => {
                this.toastService.handleError(error, `ERRORS.library_type.${projectLibraryItem.libraryType}.not_updated`);
                reject(error);
            });
        });
    }

    public deleteProjectLibraryItem(projectLibraryItem: ProjectLibraryItem): Promise<void> {
        return new Promise((resolve, reject) => {
            this.apiService.delete(`/project/delete-library-items/${projectLibraryItem.id}`).then(() => {
                resolve();
            }).catch((error) => {
                this.toastService.handleError(error, `ERRORS.library_type.${projectLibraryItem.libraryType}.not_deleted`);
                reject(error);
            });
        });
    }

    public async generateCsvExport(): Promise<void> {
        try {
            const response = await this.apiService.get<HttpResponse<Blob>>('/project/csv', 'blob');
            saveAs(response.body, `export-${new Date().getTime()}.csv`);
        } catch (error) {
            this.toastService.handleError(error, 'ERRORS.csv_generation_failed');
        }
    }

    public isOwner(project: ProjectDetails): boolean {
        return project && this.userInfo && this.userInfo.uniqueName === project.owner.emailAddress
            && this.userService.userHasProperOwnerRoles(this.userInfo);
    }

    public isProjectRiskManager(project: ProjectDetails): boolean {
        return project && this.userInfo && project.riskManager && this.userInfo.uniqueName === project.riskManager.emailAddress
            && this.userService.userHasProperOwnerRoles(this.userInfo);
    }

    public canEditProject(project: ProjectDetails): boolean {
        return project && (this.isOwner(project) || this.isProjectRiskManager(project) || this.isCompanyEditMember(project) || this.isProjectEditMember(project));
    }

    public canEditProjectDetails(project: ProjectDetails): boolean {
        return project && (this.isOwner(project) || this.isProjectRiskManager(project) || this.isCompanyEditMember(project));
    }

    public canEditProjectMembers(project: ProjectDetails): boolean {
        // Currently this is the same set
        return this.canEditProjectDetails(project);
    }

    public canEditQuickscan(project: ProjectDetails): boolean {
        return project && (this.canEditProjectDetails(project) || this.isQuickscanEditor(project));
    }

    private isQuickscanEditor(project: ProjectDetails) {
        const username = this.userInfo ? this.userInfo.uniqueName : undefined;

        return project.members.some((projectMember) => projectMember.user.emailAddress === username && projectMember.quickscanEditor);
    }

    public canEditProjectLibraryItems(project: ProjectDetails): boolean {
        return project && (this.canEditProjectDetails(project) || this.isProjectLibraryItemEditor(project));
    }

    private isProjectLibraryItemEditor(project: ProjectDetails) {
        const username = this.userInfo ? this.userInfo.uniqueName : undefined;

        return project.members.some((projectMember) => projectMember.user.emailAddress === username && projectMember.projectLibraryItemEditor);
    }

    public canEditTis(project: ProjectDetails): boolean {
        return project && (this.canEditProjectDetails(project) || this.isTisEditor(project));
    }

    private isTisEditor(project: ProjectDetails) {
        const username = this.userInfo ? this.userInfo.uniqueName : undefined;

        return project.members.some((projectMember) => projectMember.user.emailAddress === username && projectMember.tisEditor);
    }

    public canManageCompany(project: ProjectDetails) {
        if (project && this.userInfo) {
            if (this.userService.isRiskManager()
                // Being risk manager is nice, but it needs to be for the proper type.
                && this.userInfo.companyTypes.some((companyType) => companyType.id === project.companyTypeId)) {
                return true;
            }
           return this.userService.userHasProperAdminRoles(this.userInfo, project);
        }
        return false;
    }

    public isCompanyTenderManager(project: ProjectDetails): boolean {
        const username = this.userInfo ? this.userInfo.uniqueName : undefined;

        if (this.userInfo && project) {
            return this.userInfo.manageableCompanies.find(
                (company) => company.name === project.company &&
                    company.tenderManagers.find((tenderManager) => {
                        return tenderManager.emailAddress === username;
                    })
            ) !== undefined;
        }

        return false;
    }

    private isProjectEditMember(project: ProjectDetails) {
        const username = this.userInfo ? this.userInfo.uniqueName : undefined;
        return project.members.some((projectMember) => projectMember.user.emailAddress === username && projectMember.isProjectEditor());
    }

    private isCompanyEditMember(project: ProjectDetails): boolean {
        const username = this.userInfo ? this.userInfo.uniqueName : undefined;

        return this.containsEditMember(username, project.companyMembers);
    }

    private containsEditMember(username: string, members: Member[]): boolean {
        return members.find((member) => member.user.emailAddress === username && member.hasEditRights) !== undefined;
    }

    public toExecution(project: IProject): Promise<ProjectMandateDetails> {
        return this.apiService.put(`/project/${project.id}/in_execution`, project).then((projectResponse: HttpResponse<IProjectMandateDetails>) => new ProjectMandateDetails(projectResponse.body)).catch((error) => {
            this.toastService.openError(error.status, this.translateService.instant('ERRORS.project_to_execution_nok'));
            return Promise.reject(error);
        });
    }

    public toArchived(projectDetails: ProjectDetails): Promise<void> {
        return this.apiService.delete(`/project/${projectDetails.id}`);
    }

    public toClosed(projectDetails: ProjectDetails): Promise<void> {
        return this.apiService.delete(`/project/${projectDetails.id}/close`);
    }

    public toggleOnHold(projectDetails: ProjectDetails): Promise<ProjectDetails> {
        return this.apiService.put(`/project/${projectDetails.id}/on_hold`, projectDetails.toApiModel())
            .then((projectResponse: HttpResponse<IProjectDetailsData>) => {
                return new ProjectDetails(projectResponse.body);
            });
    }
}
